import { AppNode, NodeType } from "@features/builder/graph/types"
import { DECIMAL_PLACES_OPTIONS } from "@features/builder/NodeDrawer/NodeForm/UnitFields/UnitField"
import { ValidationError, getLabelFormatSchema } from "@utils/form"
import {
  createLagNodeLabel,
  isNodeLabelUnique,
  normalizeNodeLabel,
} from "@utils/node/labelValidation"
import * as yup from "yup"

export const getNodeRowSchema = (allNodes: AppNode[], nodeId: string) =>
  yup.object({
    id: yup.string().required(), // non visible, node id
    isSelected: yup.boolean().required(),
    lagValue: yup.number().nullable(), // only needed for lag nodes to correctly format the label, non editable
    label: yup.string().when(["isSelected"], ([isSelected], schema) => {
      // if not selected, we don't need to validate it further, we are not going to save this row
      if (!isSelected) return schema

      schema = schema.required(ValidationError.REQUIRED)

      const node = allNodes.find(({ id }) => id === nodeId)
      if (!node || node.type === NodeType.Group) return schema

      return getLabelFormatSchema(schema)
    }),
    link: yup.string(),
    historicMnemonic: yup.string(),
    scenarioMnemonic: yup.string(),
    forecastMnemonic: yup.string(),
    tags: yup.array().when(["isSelected"], ([isSelected], schema) => {
      // if not selected, we don't need to validate it further, we are not going to save this row
      if (!isSelected) return schema.nullable()

      return schema
        .of(
          yup.object({
            tag: yup.string().required(ValidationError.REQUIRED),
            value: yup.string().required(ValidationError.REQUIRED),
          }),
        )
        .test("are-tags-unique", "", (tags, ctx) => {
          if (!tags) return true

          const tagNames = tags.map((tag) => tag.tag)
          const nonUniqueTagNames = tagNames.filter((tag, index) => tagNames.indexOf(tag) !== index)
          const nonUniqueTags = Array.from(new Set(nonUniqueTagNames))

          if (nonUniqueTags.length === 0) return true

          return ctx.createError({ message: `Duplicated tag names: ${nonUniqueTags.join(", ")}` })
        })
        .nullable()
    }),
    graphTags: yup.array().when(["isSelected"], ([isSelected], schema) => {
      // if not selected, we don't need to validate it further, we are not going to save this row
      if (!isSelected) return schema.nullable()

      return schema
        .of(
          yup.object({
            tag: yup.string().required(ValidationError.REQUIRED),
            values: yup
              .array()
              .of(yup.string().required())
              .min(1, ValidationError.REQUIRED)
              .required(ValidationError.REQUIRED),
          }),
        )
        .test("are-graph-tags-unique", "", (tags, ctx) => {
          if (!tags) return true

          const tagNames = tags.map((tag) => tag.tag)
          const nonUniqueTagNames = tagNames.filter((tag, index) => tagNames.indexOf(tag) !== index)
          const nonUniqueTags = Array.from(new Set(nonUniqueTagNames))

          if (nonUniqueTags.length === 0) return true

          return ctx.createError({ message: `Duplicated tag names: ${nonUniqueTags.join(", ")}` })
        })
        .nullable()
    }),
    unitShowAsPercentage: yup.boolean().required(),
    unitSuffix: yup.string().nullable(),
    unitDecimalPlaces: yup
      .string()
      .when(["isSelected", "unitSuffix"], ([isSelected, suffix], schema) => {
        // if not selected, we don't need to validate it further, we are not going to save this row
        if (!isSelected) return schema

        // having suffix is not enough to created full unit format, so we need to require decimal places as well
        if (suffix) {
          return schema.oneOf(DECIMAL_PLACES_OPTIONS).required(ValidationError.REQUIRED)
        }

        return schema
      }),
  })

export type BulkNodesEditFormNodeRowValues = yup.InferType<ReturnType<typeof getNodeRowSchema>>

export const getValidationSchema = (allNodes: AppNode[]) => {
  const nodesSchema = yup.lazy((value) => {
    const nodesRowsById = (value as Record<string, BulkNodesEditFormNodeRowValues>) ?? {}
    const nodesShape = Object.fromEntries(
      Object.keys(nodesRowsById).map((nodeId) => [nodeId, getNodeRowSchema(allNodes, nodeId)]),
    )

    return yup
      .object(nodesShape)
      .required()
      .test("is-node-label-unique", "", function (nodesById, context) {
        const nodesToValidate = allNodes.filter((node) => node.type !== NodeType.Group)
        const labelsToValidate = nodesToValidate.map((node) => {
          const nodeRow = nodesById[node.id]
          const nodeRowLabel =
            node.type === NodeType.Lag
              ? createLagNodeLabel(nodeRow?.label, nodeRow?.lagValue ?? 0)
              : nodeRow?.label

          return nodeRow && nodeRow.isSelected ? nodeRowLabel : node.data.label
        })
        const normalizedLabels = labelsToValidate.map(normalizeNodeLabel)
        const duplicatedLabels = normalizedLabels.filter(
          (label, index) =>
            label !== "" &&
            !isNodeLabelUnique(
              label,
              normalizedLabels.filter((_otherLabel, otherIndex) => otherIndex !== index),
            ),
        )

        if (duplicatedLabels.length === 0) return true

        return context.createError({
          message: `Duplicated node labels: ${duplicatedLabels.join(", ")}`,
        })
      })
  })

  return yup.object({
    nodes: nodesSchema,
  })
}
